import React, { useState, useRef, useEffect } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, Loader2, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "How many U12 players do we have?",
  "Which players have unpaid fees this month?",
  "Show players born in 2014.",
  "Which category has the most players?",
  "Which players are missing documents?",
  "Give me a summary of this month's academy activity.",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await client.post("/ai/assistant", { query: q });
      setMessages((m) => [...m, { role: "ai", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "⚠️ " + apiError(e) }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader title="AI Assistant" subtitle="Ask questions about your academy data — grounded on real records" testid="ai-page" />
      <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 sm:p-6 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="h-16 w-16 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-4"><Sparkles size={30} /></div>
              <h3 className="text-xl font-bold font-heading">Academy AI Assistant</h3>
              <p className="text-muted-foreground max-w-md mt-2">Ask anything about players, payments, attendance or documents. Answers use only your academy data.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6 max-w-2xl">
                {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="text-start text-sm p-3 rounded-lg border border-border hover:border-emerald-500 hover:bg-emerald-500/5 transition-colors" data-testid={`ai-suggest`}>{s}</button>)}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "ai" && <div className="h-8 w-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0"><Bot size={18} /></div>}
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-muted"}`}>{m.text}</div>
            </div>
          ))}
          {loading && <div className="flex gap-3"><div className="h-8 w-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center"><Bot size={18} /></div><div className="bg-muted rounded-2xl px-4 py-2.5"><Loader2 className="animate-spin" size={16} /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your academy..." data-testid="ai-input" />
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => send()} disabled={loading} data-testid="ai-send"><Send size={16} /></Button>
        </div>
      </Card>
    </div>
  );
}
